import { useState } from 'react';
import { Home } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface HeroPhotoProps {
  /** Image URL. `null` triggers the placeholder fallback. */
  src: string | null;
  /** Required alt text. Conventionally the property address. */
  alt: string;
  /**
   * Whether to prioritize loading. Defaults to `true` — all hero images
   * load eagerly by default. With ~60 cards × ~300KB this is well under
   * any browser's parallel-fetch budget, and the perceived UX of
   * always-loaded thumbnails is worth the ~18MB up-front cost. Pass
   * `priority={false}` to opt back into native lazy loading.
   */
  priority?: boolean;
  className?: string;
}

/**
 * `<HeroPhoto>` — the 4:3 photo on the left of `<PropertyCard>`.
 *
 * Anatomy (UI-SPEC § 3.3):
 * - 440×330 in long-row layout (`md:` and up).
 * - 16:10 aspect ratio in the mobile vertical-stack layout.
 * - `loading="eager"` by default (Phase 08-r — prior `lazy` default left
 *   the dashboard visibly blank for several seconds; with ~60 cards × ~300KB
 *   total bandwidth the eager fetch finishes in seconds on modern broadband).
 *   Pass `priority={false}` to opt back into `lazy`.
 * - `decoding="async"` always; explicit `width`/`height` to prevent CLS.
 * - On image error or `src=null`: render `bg-ocean-800` panel with a
 *   centred lucide `Home` icon at `text-foam-50/40`.
 *
 * The slight `scale-105` hover transform belongs on the card parent (it
 * uses `group-hover:scale-105`); the `<img>` here exposes the same class
 * so consumers can toggle it via the parent's `group` class.
 */
export function HeroPhoto({ src, alt, priority = true, className }: HeroPhotoProps) {
  const [errored, setErrored] = useState(false);
  const showFallback = !src || errored;

  if (showFallback) {
    return (
      <div
        role="img"
        aria-label={alt || 'Property photo unavailable'}
        className={cn(
          'w-full h-full bg-ocean-800 flex items-center justify-center',
          'aspect-[16/10] md:aspect-[4/3]',
          className,
        )}
      >
        <Home aria-hidden className="w-12 h-12 text-foam-50/40" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      width={440}
      height={330}
      onError={() => setErrored(true)}
      className={cn(
        'w-full h-full object-cover transition-transform duration-200 group-hover:scale-105',
        'aspect-[16/10] md:aspect-[4/3]',
        className,
      )}
    />
  );
}
